import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Table, Badge, Input, Select, Button, Field, SortControl, sortRows } from "@/lib/ui";

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState({ search: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "company_name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "company_name", label: "Company", type: "str" },
    { key: "seller_type", label: "Type", type: "str" },
    { key: "plan", label: "Package", type: "str" },
    { key: "status", label: "Status", type: "str" },
    { key: "start", label: "Start", type: "date" },
    { key: "expiry", label: "Expiry", type: "date" },
  ];

  const load = useCallback(() => { api.get("/admin/subscriptions").then(({ data }) => setRows(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) =>
    (!q.search || (r.company_name + r.phone + (r.plan || "")).toLowerCase().includes(q.search.toLowerCase())) &&
    (!q.status || r.status === q.status));

  const saveEdit = async () => {
    try {
      await api.put(`/admin/sellers/${editing.seller_id}`, {
        subscription_plan: editing.plan || "", subscription_status: editing.status || "inactive",
      });
      toast.success("Subscription updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Subscriptions</h1>
      <p className="mt-1 text-sm text-slate-500">Which seller has taken which package. Edit a seller's plan or status inline.</p>
      <div className="mt-4 mb-4 flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="Search company / mobile / plan…" value={q.search} onChange={(e) => setQ((p) => ({ ...p, search: e.target.value }))} data-testid="subs-search" />
        <Select className="w-auto" value={q.status} onChange={(e) => setQ((p) => ({ ...p, status: e.target.value }))} data-testid="subs-status">
          <option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="expired">Expired</option>
        </Select>
        <Button variant="outline" onClick={() => setQ({ search: "", status: "" })}><i className="fa-solid fa-rotate-left" /> Reset</Button>
        <div className="ml-auto"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="subs-sort" /></div>
      </div>
      <Table head={["Company", "Type", "Mobile", "Package", "Status", "Start", "Expiry", "Actions"]}>
        {sortRows(filtered, SORT_FIELDS, sort).map((r) => (
          <tr key={r.seller_id} data-testid={`sub-${r.seller_id}`}>
            <td className="px-4 py-3 font-semibold text-slate-800">{r.company_name}</td>
            <td className="px-4 py-3 capitalize">{r.seller_type}</td>
            <td className="px-4 py-3">{r.phone}</td>
            <td className="px-4 py-3">{r.plan || <span className="text-slate-400">No package</span>}</td>
            <td className="px-4 py-3"><Badge tone={r.status === "active" ? "green" : r.status === "expired" ? "red" : "slate"}>{r.status}</Badge></td>
            <td className="px-4 py-3 text-slate-500">{r.start ? new Date(r.start).toLocaleDateString() : "—"}</td>
            <td className="px-4 py-3 text-slate-500">{r.expiry ? new Date(r.expiry).toLocaleDateString() : "—"}</td>
            <td className="px-4 py-3">
              <Button variant="outline" onClick={() => setEditing({ seller_id: r.seller_id, company_name: r.company_name, plan: r.plan || "", status: r.status || "inactive" })} data-testid={`sub-edit-${r.seller_id}`}><i className="fa-solid fa-pen" /></Button>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No subscriptions</td></tr>}
      </Table>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="sub-edit-modal">
            <h2 className="font-head text-xl font-extrabold text-slate-900">Edit Subscription</h2>
            <p className="text-xs text-slate-400">{editing.company_name}</p>
            <div className="mt-4 space-y-3">
              <Field label="Package Plan"><Input value={editing.plan} onChange={(e) => setEditing((s) => ({ ...s, plan: e.target.value }))} placeholder="e.g. Premium Annual" data-testid="sub-edit-plan" /></Field>
              <Field label="Status"><Select value={editing.status} onChange={(e) => setEditing((s) => ({ ...s, status: e.target.value }))} data-testid="sub-edit-status"><option value="active">active</option><option value="inactive">inactive</option><option value="expired">expired</option></Select></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} data-testid="sub-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
